import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Space, message, Input, Select, DatePicker, Form, Pagination, Typography } from 'antd';
import { HeartFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatAmountText } from '../../utils/numberFormat';
import { DebtTypeOptions, toSelectOptions, labelOfDebtType, labelOfDisposalMethod, formatTransferConditionText } from '../../constants/debtEnums';
import { useNavigate } from 'react-router-dom';
import { getFavorites, removeFavorite as apiRemoveFavorite } from '../../services/debt';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './FavoritesPage.css';

/**
 * 收藏夹
 * 职责：展示用户收藏的债权列表，支持筛选、排序、查看详情、取消收藏。
 * 数据：从服务层拉取收藏列表与详情；取消收藏后本地同步移除并更新总数。
 * 交互：表格分页排序、详情跳转、取消收藏操作。
 */
const FavoritesPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [sorter, setSorter] = useState({ field: 'favorite_update_time', order: 'desc' });
  const [searchForm] = Form.useForm();

  // 拉取列表：携带筛选与排序参数，更新分页与数据集
  const loadList = async (page = 1, pageSize = PAGINATION_DEFAULT_PAGE_SIZE, sort = sorter, knownTotal = pagination.total) => {
    try {
      setLoading(true);
      const effectivePageSize = normalizePageSize(pageSize);
      const values = searchForm.getFieldsValue();
      const params = {
        page,
        size: effectivePageSize,
        keyword: values.keyword || undefined,
        debt_type: values.debt_type || undefined,
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sort.field,
        sort_order: sort.order,
        need_total: page === 1,
      };
      const { data } = await getFavorites(params);
      const list = Array.isArray(data?.records) ? data.records : [];
      const totalFromApi = data?.total;
      const total = Number(totalFromApi ?? knownTotal ?? 0);
      setRecords(list);
      setPagination(prev => ({ ...prev, current: page, pageSize: effectivePageSize, total }));
    } catch (e) {
      message.error('加载收藏夹失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 查询与重置：重置到第一页，恢复默认排序并重新加载
  const handleSearch = () => { setPagination(prev => ({ ...prev, current: 1 })); loadList(1, pagination.pageSize, sorter); };
  const handleReset = () => { searchForm.resetFields(); setSorter({ field: 'favorite_update_time', order: 'desc' }); loadList(1, pagination.pageSize, { field: 'favorite_update_time', order: 'desc' }); };

  // 取消收藏：调用服务端接口，本地移除记录并更新分页总数
  const cancelFavorite = async (id) => {
    try {
      await apiRemoveFavorite(id);
      message.success('已取消收藏');
      setRecords(prev => prev.filter(r => r.id !== id));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (e) {
      message.error('操作失败');
    }
  };

  // 查看详情：跳转到统一的债权详情页
  const openDetail = (record) => {
    const id = record?.id;
    if (!id) return;
    navigate(`/debt-management/detail/${id}`);
  };

  const actionColumnWidth = isMobile ? 140 : 240;

  const columns = [
    {
      title: '债权名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v, record) => (
        <Typography.Link className="u-ellipsis-inline font-medium" onClick={() => openDetail(record)}>
          {v || '-'}
        </Typography.Link>
      )
    },
    { title: '债权类型', dataIndex: 'debt_type', key: 'debt_type', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '合计金额', dataIndex: 'total_amount', key: 'total_amount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '债权发生日', dataIndex: 'debt_occurrence_date', key: 'debt_occurrence_date', width: 140, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '处置方式', dataIndex: 'disposal_method', key: 'disposal_method', width: 140, render: (v) => labelOfDisposalMethod(v) },
    { title: '转让条件', key: 'transfer_condition', width: 220, ellipsis: true, render: (_, record) => formatTransferConditionText(record, { formatAmount: formatAmountText }) },
    { title: '收藏时间', dataIndex: 'favorite_update_time', key: 'favorite_update_time', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '发布时间', dataIndex: 'publish_time', key: 'publish_time', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: actionColumnWidth,
      render: (_, record) => (
        <div className="c-actions-inline c-actions-cell" style={{ '--actions-cell-width': isMobile ? '140px' : '240px' }}>
          <Button
            danger
            type="link"
            className="c-btn-link-compact"
            onClick={() => cancelFavorite(record.id)}
            icon={<HeartFilled />}
            aria-label="取消收藏"
          >
            {isMobile ? '' : '取消收藏'}
          </Button>
        </div>
      ),
    },
  ];

  // 表格交互：同步分页与排序并重新请求数据
  const handleTableChange = (_pag, _filters, sorterArg) => {
    const nextOrder = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const nextField = sorterArg?.field || sorter.field;
    const nextSorter = { field: nextField, order: nextOrder };
    setSorter(nextSorter);
    loadList(pagination.current, pagination.pageSize, nextSorter, pagination.total);
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    setPagination(prev => ({ ...prev, current: nextCurrent, pageSize: nextPageSize }));
    loadList(nextCurrent, nextPageSize, sorter, pagination.total);
  };

  return (
    <div className="page-container p-debt-marketplace-favorites">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">收藏夹</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={searchForm} onFinish={handleSearch}>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="债权名称/编号" allowClear style={{ width: isMobile ? '100%' : 200 }} />
          </Form.Item>
          <Form.Item name="debt_type" label="类型">
            <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
          </Form.Item>
          <Form.Item name="date_range" label="发生日">
            <DatePicker.RangePicker allowEmpty={[true, true]} style={{ width: isMobile ? '100%' : undefined }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

        <Card className="card-compact">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FavoritesPage;
